
import {throwError as observableThrowError ,  from as fromPromise } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpParams } from '@angular/common/http';
import { AppHelperService } from './app-helper.service';


import Raven from 'raven-js';
import { config } from 'config';
import { appSetting } from 'config/appSettings';
import { GigyaSessionService } from './gigya-session.service';
import { environment } from 'environments/environment';
import { DesktopAuthService } from 'modules/desktop/services/desktop-auth.service';
import { ApiGatewayHelperService } from './apigateway-helper.service';

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {

    private desktopAuthService: DesktopAuthService;
    
    constructor(
        private readonly gigyaSessionService: GigyaSessionService,
        injector: Injector,
        private readonly apiGatewayHelperService: ApiGatewayHelperService,
        private readonly appHelperService: AppHelperService
    ) {

        // there is a circular dependency where in we need dekstop auth service to logout in case of error and
        // in desktop auth service we need http client module to actually logout from server.
        // Even though we have removed all the circular dependencies, we chose to leave this as
        // resolving this might need major refactor.
        setTimeout(() => {
            this.desktopAuthService = injector.get(DesktopAuthService);
        }, 100)
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): any {
        
        // writing 2 functions with similar code to resolve promise/observable conflict due to time crunch
        // needs to be corrected
        // one function should cater to both desktop and webapp
        if(environment.isDesktopApp){
            // convert promise to observable using 'from' operator
            return fromPromise(this.modifyReqForDesktop(req, next));
        }else{
            return this.modifyReqForWebApp(req, next);
        }
        
    }

    modifyReqForWebApp(req: HttpRequest<any>, next: HttpHandler) {
        let newClonedReq;
        let reqObj;
        if (req.method == 'GET' && this.isTimestampNeeded(req.url)) {
            const timestamp = new Date().getTime();
            let timestampQueryParam;
            if (req.url.indexOf('?') !== -1) {
                timestampQueryParam = '&timestamp=' + timestamp;
            } else {
                timestampQueryParam = '?timestamp=' + timestamp;
            }
            reqObj = {
                setHeaders: {
                    [config.customReqHeaders.appVersion.key]: config.customReqHeaders.appVersion.value
                },
                url: req.url.concat(timestampQueryParam),
                withCredentials: true
            }
            reqObj = this.addRoleHeaderToRequestObject(reqObj);
        } else {
            //Standard CORS requests do not send or set any cookies by default.
            //In order to set/send cookie we need to set the .withCredentials property to true.
            reqObj = {
                setHeaders: {
                    [config.customReqHeaders.appVersion.key]: config.customReqHeaders.appVersion.value
                },
                withCredentials: true
            }
            reqObj = this.addRoleHeaderToRequestObject(reqObj);
        }
        newClonedReq = req.clone(reqObj);

        Raven.captureBreadcrumb({
            message: '[Custom] captured new angular http request...',
            category: 'angular-http',
            data: newClonedReq,
            level: 'info'
        });

        //send the newly created request
        return next.handle(newClonedReq)
            .pipe(
                catchError((errorObj) => {
                    return this.handleError(newClonedReq, next, errorObj);
                })
            );
    }

    /**
     * Making async as need to use await for creating mobile-gateway auth keys
     * @returns 
     */
    async modifyReqForDesktop(req: HttpRequest<any>, next: HttpHandler) {
        let newClonedReq;
        let reqObj;
        if (req.method == 'GET' && this.isTimestampNeeded(req.url)) {
            const timestamp = new Date().getTime();
            let timestampQueryParam;
            if (req.url.indexOf('?') !== -1) {
                timestampQueryParam = '&timestamp=' + timestamp;
            } else {
                timestampQueryParam = '?timestamp=' + timestamp;
            }
            reqObj = {
                setHeaders: {
                    [config.customReqHeaders.appVersion.key]: config.customReqHeaders.appVersion.value
                },
                url: req.url.concat(timestampQueryParam),
                withCredentials: true
            }
            reqObj = this.addRoleHeaderToRequestObject(reqObj);
        } else {
            //Standard CORS requests do not send or set any cookies by default.
            //In order to set/send cookie we need to set the .withCredentials property to true.
            reqObj = {
                setHeaders: {
                    [config.customReqHeaders.appVersion.key]: config.customReqHeaders.appVersion.value
                },
                url: req.url,
                withCredentials: true
            }
            reqObj = this.addRoleHeaderToRequestObject(reqObj);
        }

        if(environment.isDesktopApp){
            let token = await this.apiGatewayHelperService.getAPIAuthToken();
            reqObj.url = this.addQueryParams(reqObj.url, token)
        }

        newClonedReq = req.clone(reqObj);

        Raven.captureBreadcrumb({
            message: '[Custom] captured new angular http request...',
            category: 'angular-http',
            data: newClonedReq,
            level: 'info'
        });

        //send the newly created request
        return next
            .handle(newClonedReq)
            .toPromise()
            .catch((errorObj) => {
                return this.handleError(newClonedReq, next, errorObj);
            }) as any;
    }

    addQueryParams(url = "", params?){
        if(params){
            let qStr = Object.keys(params).reduce(((acc, key) => (acc.concat([key, params[key]].join("=")))), []).join("&");
            if(qStr){
                if (url.indexOf('?') !== -1) {
                    url = `${url}&${qStr}`;
                } else {
                    url = `${url}?${qStr}`;
                }
            }
        }

        return url;
    }

    isTimestampNeeded(url) {
        const isFile = url.substring(url.length - 5, url.length);
        // Caching of all files except html is avoided in order to make sure changes are reflected everytime.
        if (isFile === '.html') {
            return false;
        }
        return true;
    }

    addRoleHeaderToRequestObject(reqObj) {
        let userData: any = sessionStorage.getItem(appSetting.sessionStorage.userAccount);
        if (userData) {
            userData = JSON.parse(userData);
            if (userData.role) reqObj.setHeaders[config.customReqHeaders.userRole.key] = userData.role;
            if(userData.extUserId) reqObj.setHeaders[config.customReqHeaders.userId.key] = userData.extUserId;
        }
        return reqObj;
    }
    // Interceptor Error Handling 
    handleError(newClonedReq, next, errorObj, tryReLogin = true) {
        Raven.captureBreadcrumb({
            message: '[Custom] captured failed angular http request...',
            category: 'angular-http',
            data: errorObj,
            level: 'error'
        });
        if(errorObj.status === 403 && errorObj.error.code === 'CLASS_DELETED') {
            sessionStorage.setItem('cupa.c1.classInvitationError.classDeleted', 'true');
            this.appHelperService.showLoaderAndNavigateToDashboard(null, null, null);
        } else if (environment.isDesktopApp && errorObj.status == 401 && errorObj.error.errorCode == 'AUTHORIZATION_ERROR') {
            // if auth error occurs in desktop app, then take user to login page
            localStorage.setItem(`${appSetting.localStorage.foc}.AUTH_ERROR`, "true");
            return fromPromise(this.desktopAuthService.logOut(true));
        } else if (environment.isDesktopApp && errorObj.status == "error" && errorObj.error == "INVALID_USER_ROLE") {
            // logout user if some non-teacher tried to access teacher data
            return fromPromise(this.desktopAuthService.logOut(false, 700));
        } else if (tryReLogin && errorObj.status == 401 && errorObj.error.errorCode == 'AUTHORIZATION_ERROR') {
            // if user is unauthorized try to login again in dls session using gigya data.
            return this.unAuthorizedUserReLogin(newClonedReq, next);
        } else if (errorObj.status == 503 || (errorObj.status == 403 && errorObj.error.code == 'STALE_REQUEST')) {
            window.location.reload();
        } else if (errorObj.status == 401 && errorObj.error.errorCode == 'USER_MISMATCH_ERROR') {
            sessionStorage.removeItem(appSetting.sessionStorage.userAccount);
            window.location.reload();
        } else if (errorObj.status == 0) {
            //Update the errorObj with custom Error to override the cyclic reference.
            let customErrorObj = new Error(errorObj.message);
            customErrorObj['status'] = errorObj.status;
            errorObj = customErrorObj;
        }
        //return the error to the method that called it
        return observableThrowError(errorObj);
    }

    /**
     * Get userData from gigya and send the Clone of failed request appending userData in QueryParams
     * The userData is base64 encoded.
     * 
     * @param {*} newClonedReq
     * @param {*} next
     * @returns {Observable} Clone of Request with userData appended to QueryParams for session initialization
     * @memberof CustomHttpInterceptor
     */
    unAuthorizedUserReLogin(newClonedReq, next) {
        return fromPromise(this.getUserDataFromGigya())
            .pipe(
                switchMap(userData => {
                    return next.handle(newClonedReq.clone(this.addUserDataToQueryParams(newClonedReq, userData)))
                        .pipe(catchError((errorObj) => {
                            return this.handleError(newClonedReq, next, errorObj, false);
                        }));
                })
            )
    }

    addUserDataToQueryParams(req, userData) {
        let newParams = new HttpParams({ fromString: req.params.toString() });
        //Encode the string into base64 encoding
        let data = btoa(encodeURIComponent(JSON.stringify(userData)));
        //Encode the param to handle the following characters: , / ? : @ & = + $ #
        newParams = newParams.append('userData', encodeURIComponent(data));
        return { params: newParams };
    }

    async getUserDataFromGigya() {
        const gigyaData = await this.gigyaSessionService.getUserData();
        if (gigyaData) {
            const userData: any = {
                extUserId: gigyaData.UID,
                role: gigyaData.c1UserRole,
                email: gigyaData.profile.email,
                firstName: gigyaData.profile.firstName,
                lastName: gigyaData.profile.lastName
            }
            if (gigyaData.profile.email && gigyaData.profile.email.indexOf('@cambridge.org') != -1) {
                const samlData: any = await this.getUserSAMLData();
                if (samlData) {
                    if (samlData.eltCustomerServices == "true" || samlData.eltSuperAdmin == "true" || samlData.eltSales == "true") {
                        userData.samlData = samlData;
                    } else {
                        window['gigya'].accounts.logout();
                    }
                }
            }
            return userData;
        } else {
            this.gigyaSessionService.gigyaLogout();
            window.location.href = config.onBoardingAppDomain + 'login';
            return Promise.reject(new Error("Gigya session not found."));
        }
    }
    async getUserSAMLData() {
        return new Promise((resolve) => {
            window['gigya'].accounts.getAccountInfo({
                extraProfileFields: 'samlData',
                callback: (res) => {
                    resolve(res.profile.samlData);
                }
            })
        });
    }
}